import type {
  ApiResponse,
  CursorPaginatedResponse,
  EmailTemplate,
  EmailTemplateListParams,
  SMSTemplate,
  SMSTemplateListParams,
} from '@/libs/interfaces'

import { mockEmailTemplates, mockSMSTemplates } from '../fixtures/communications'
import { MOCK_ORG_ID } from '../fixtures/seed'
import { delay, genID, matchesSearch, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const emailTemplateStore = createEntityStore<EmailTemplate, 'emailTemplateID'>(
  mockEmailTemplates,
  'emailTemplateID',
)
const smsTemplateStore = createEntityStore<SMSTemplate, 'smsTemplateID'>(
  mockSMSTemplates,
  'smsTemplateID',
)

const toApiResponse = <T>(data: T): ApiResponse<T> => ({ data, status: 200 })

const filterEmailTemplates = (params: EmailTemplateListParams): EmailTemplate[] =>
  emailTemplateStore
    .getAll()
    .filter((template) => matchesSearch(template, params.search, ['templateTitle', 'templateName']))

const filterSMSTemplates = (params: SMSTemplateListParams): SMSTemplate[] =>
  smsTemplateStore
    .getAll()
    .filter((template) => matchesSearch(template, params.search, ['templateTitle', 'templateName']))

export const communicationApiMock = {
  // Email Templates
  createEmailTemplate: async (
    data: Partial<EmailTemplate>,
  ): Promise<ApiResponse<EmailTemplate>> => {
    const emailTemplate: EmailTemplate = {
      emailTemplateID: genID('email-template'),
      orgID: MOCK_ORG_ID,
      templateTitle: '',
      templateName: '',
      templateContent: '',
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...data,
    }
    emailTemplateStore.insert(emailTemplate)
    return delay(toApiResponse(emailTemplate))
  },
  deleteEmailTemplate: async (
    _orgID: string,
    emailTemplateID: string,
  ): Promise<ApiResponse<void>> => {
    emailTemplateStore.remove(emailTemplateID)
    return delay(toApiResponse(undefined))
  },
  getEmailTemplate: async (_orgID: string, emailTemplateID: string): Promise<EmailTemplate> =>
    delay(emailTemplateStore.getById(emailTemplateID) ?? {}),
  listEmailTemplatesCursor: async (
    params: EmailTemplateListParams,
  ): Promise<CursorPaginatedResponse<EmailTemplate>> =>
    delay(paginate(filterEmailTemplates(params), params)),
  updateEmailTemplate: async (payload: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    const updated = payload.emailTemplateID
      ? emailTemplateStore.update(payload.emailTemplateID, { ...payload, updatedAt: nowISO() })
      : undefined
    return delay(updated ?? (payload as EmailTemplate))
  },

  // SMS Templates
  createSMSTemplate: async (data: Partial<SMSTemplate>): Promise<ApiResponse<SMSTemplate>> => {
    const smsTemplate: SMSTemplate = {
      smsTemplateID: genID('sms-template'),
      orgID: MOCK_ORG_ID,
      templateTitle: '',
      templateName: '',
      templateContent: '',
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...data,
    }
    smsTemplateStore.insert(smsTemplate)
    return delay(toApiResponse(smsTemplate))
  },
  deleteSMSTemplate: async (_orgID: string, smsTemplateID: string): Promise<ApiResponse<void>> => {
    smsTemplateStore.remove(smsTemplateID)
    return delay(toApiResponse(undefined))
  },
  getSmsTemplate: async (_orgID: string, smsTemplateID: string): Promise<SMSTemplate> =>
    delay(smsTemplateStore.getById(smsTemplateID) ?? {}),
  listSMSTemplatesCursor: async (
    params: SMSTemplateListParams,
  ): Promise<CursorPaginatedResponse<SMSTemplate>> =>
    delay(paginate(filterSMSTemplates(params), params)),
  updateSmsTemplate: async (payload: Partial<SMSTemplate>): Promise<SMSTemplate> => {
    const updated = payload.smsTemplateID
      ? smsTemplateStore.update(payload.smsTemplateID, { ...payload, updatedAt: nowISO() })
      : undefined
    return delay(updated ?? (payload as SMSTemplate))
  },
}
